import api from './api'
import type {
  AdminUser,
  CareerRecordRow,
  Champion,
  CoachStats,
  Conference,
  ConferenceDraftProposal,
  GameDetail,
  GameSummary,
  GameTypeFilter,
  HeadToHeadMatrix,
  HeadToHeadRow,
  ImportSummary,
  LeaguePlayer,
  LeaguePlayerAliases,
  LeagueSnapshot,
  PlayerProfile,
  PlayerSchedule,
  PlayoffBracket,
  PlayoffChampionAdvisory,
  PlayoffFormat,
  PlayoffPairing,
  PlayoffGameCandidate,
  PlayoffSeasonSummary,
  PlayoffSeedSuggestions,
  PlayoffSeriesSide,
  RosterPlayerGameLog,
  RosterPlayerStats,
  Season,
  SeasonDeadlines,
  SeasonDetail,
  SeasonMembership,
  SeasonRecordBreakdown,
  SeasonSignup,
  SeasonSignupStatus,
  CurrentSeasonRespins,
  SeasonStatus,
  StandingsResponse,
  UploadLogEntry,
  UploadResult,
  LeagueUpdate,
  Poll,
  PollDuration,
  PollOptionDraft,
} from '../types'

/** Typed wrappers around the RTL API. One function per endpoint. */

// --- Public reads ----------------------------------------------------------

export async function fetchSeasons(): Promise<Season[]> {
  const { data } = await api.get<Season[]>('/seasons')
  return data
}

export async function fetchSeason(number: number): Promise<SeasonDetail> {
  const { data } = await api.get<SeasonDetail>(`/seasons/${number}`)
  return data
}

/**
 * The season signups would be for, whether they are open, and whether the
 * caller is already in.
 *
 * @remarks
 * Safe to call signed out — an anonymous visitor gets the season number and the
 * headcount with `signedUp: false`, which is what labels the home page button
 * before anyone has signed in. `open` is false while a season is in progress.
 */
export async function fetchSignupStatus(): Promise<SeasonSignupStatus> {
  const { data } = await api.get<SeasonSignupStatus>('/seasons/signups')
  return data
}

/**
 * Signs the caller up for the upcoming season.
 *
 * @returns The pool as the caller now sees it.
 * @throws A 401 when nobody is signed in; send visitors through Discord first.
 *
 * @remarks
 * Signing up twice is a no-op rather than an error, so a double click costs
 * nothing.
 */
export async function signUpForSeason(): Promise<SeasonSignupStatus> {
  const { data } = await api.post<SeasonSignupStatus>('/seasons/signups')
  return data
}

/** Withdraws the caller's own signup. Withdrawing twice is a no-op. */
export async function withdrawFromSeason(): Promise<SeasonSignupStatus> {
  const { data } = await api.delete<SeasonSignupStatus>('/seasons/signups')
  return data
}

export async function fetchStandings(seasonNumber?: number): Promise<StandingsResponse> {
  const { data } = await api.get<StandingsResponse>('/standings', {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

/**
 * Lifetime records for every player with a game in scope.
 *
 * @param type - Which games to count; omit for `all`, the postseason-inclusive
 * total a career has always meant here. Filtered in SQL, so `regular` and
 * `playoffs` sum back to `all` for every player rather than being two views of
 * one prefetched list.
 *
 * @remarks
 * Championship and runner-up counts are the same under every scope: they come
 * from the Hall of Champions rather than from the games being counted.
 */
export async function fetchCareerRecords(type?: GameTypeFilter): Promise<CareerRecordRow[]> {
  const { data } = await api.get<CareerRecordRow[]>('/records/career', {
    params: type ? { type } : undefined,
  })
  return data
}

export async function fetchHeadToHeadMatrix(seasonNumber?: number): Promise<HeadToHeadMatrix> {
  const { data } = await api.get<HeadToHeadMatrix>('/records/h2h', {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

export async function fetchHeadToHead(
  leaguePlayerId: number,
  seasonNumber?: number
): Promise<HeadToHeadRow[]> {
  const { data } = await api.get<HeadToHeadRow[]>('/records/h2h', {
    params: { player: leaguePlayerId, ...(seasonNumber ? { season: seasonNumber } : {}) },
  })
  return data
}

/**
 * The signed-in player's round-robin card for a season.
 *
 * @param seasonNumber - Season to load, or omit for the current season.
 * @throws A 401 when nobody is signed in; callers should gate on the session.
 *
 * @remarks
 * Backs both the schedule page and the Discord tag list; the latter filters the
 * returned fixtures down to `played === false`.
 */
export async function fetchMySchedule(seasonNumber?: number): Promise<PlayerSchedule> {
  const { data } = await api.get<PlayerSchedule>('/records/my-schedule', {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

export async function fetchPlayerHistory(
  leaguePlayerId: number
): Promise<SeasonRecordBreakdown[]> {
  const { data } = await api.get<SeasonRecordBreakdown[]>(
    `/records/players/${leaguePlayerId}/history`
  )
  return data
}

/**
 * Competitor identities for a player picker.
 *
 * @param season - Limit to one season's conference roster; `'current'` follows
 * whichever season the league has open. Omit to get every identity on record,
 * which is what the admin screens and the head-to-head lookup need to reach
 * back through the imported history.
 *
 * @example
 * ```typescript
 * await fetchLeaguePlayers()          // everyone, ever
 * await fetchLeaguePlayers('current') // only players active this season
 * ```
 */
export async function fetchLeaguePlayers(
  season?: number | 'current'
): Promise<LeaguePlayer[]> {
  const { data } = await api.get<LeaguePlayer[]>('/players', {
    params: season === undefined ? undefined : { season },
  })
  return data
}

/**
 * One player's career page payload.
 *
 * @param leaguePlayerId - Roster id, which is what every player link on the
 * site carries. An id that a merge has since deleted still resolves; compare
 * `player.leaguePlayerId` against the one you asked for and replace the URL
 * when they differ.
 * @throws A 404 when the id names nobody.
 */
export async function fetchPlayerProfile(leaguePlayerId: number): Promise<PlayerProfile> {
  const { data } = await api.get<PlayerProfile>(`/players/${leaguePlayerId}`)
  return data
}

/**
 * One player's statistical categories for a single scope.
 *
 * @param seasonNumber - Season to scope to, or `'all'`/omitted for all-time.
 * @returns Null when the player played nothing in scope.
 *
 * @remarks
 * Backed by the same aggregation as the Stats page, so a figure here and the
 * player's row on that page's leaderboard for the same season always agree.
 */
export async function fetchPlayerStats(
  leaguePlayerId: number,
  seasonNumber?: number | 'all'
): Promise<CoachStats | null> {
  const { data } = await api.get<CoachStats | null>(`/players/${leaguePlayerId}/stats`, {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

export interface GameQuery {
  season?: number | 'all'
  conference?: 'AFC' | 'NFC'
  player?: number
  /** Meetings between this player and `player`; requires `player`. */
  opponent?: number
  status?: 'pending' | 'official' | 'void'
  /**
   * Regular season, postseason, or both. Filtered in SQL, so it narrows what
   * `limit` cuts from rather than what survives it.
   */
  type?: GameTypeFilter
  limit?: number
}

export async function fetchGames(query: GameQuery = {}): Promise<GameSummary[]> {
  const { data } = await api.get<GameSummary[]>('/games', { params: query })
  return data
}

/**
 * Admin Games list, including voided results the public log hides.
 */
export async function fetchAdminGames(query: GameQuery = {}): Promise<GameSummary[]> {
  const { data } = await api.get<GameSummary[]>('/admin/games', { params: query })
  return data
}

export async function fetchGame(id: number): Promise<GameDetail> {
  const { data } = await api.get<GameDetail>(`/games/${id}`)
  return data
}

export async function fetchStats(seasonNumber?: number | 'all'): Promise<CoachStats[]> {
  const { data } = await api.get<CoachStats[]>('/stats', {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

/**
 * Leaderboard rows for the NES roster players, not the league members.
 *
 * @param seasonNumber - Season to scope to, or `'all'`/omitted for all-time.
 * @returns One row per roster player who recorded something in scope; empty for
 * a season with no parsed box scores, which is every season before 33.
 *
 * @remarks
 * Pooled across every member who used the team, so a row is the athlete's
 * league-wide production rather than any one member's.
 */
export async function fetchRosterPlayerStats(
  seasonNumber?: number | 'all'
): Promise<RosterPlayerStats[]> {
  const { data } = await api.get<RosterPlayerStats[]>('/stats/players', {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

/**
 * One in-game player's game log, with the league member behind each stat line.
 *
 * @param teamAbbreviation - The team as the box score recorded it, e.g. `ATL`.
 * Required, and not merely a filter: rosters are team-specific, so team plus
 * name is the identity the leaderboard aggregates under and anything less would
 * pool two different athletes.
 * @param name - The in-game player's name exactly as the leaderboard renders
 * it. Periods and apostrophes are fine; axios encodes the value.
 * @param seasonNumber - Season to scope to, or `'all'`/omitted for all-time.
 * @returns Always a payload, never a 404: a scope with no box score for this
 * player returns an empty `games` array, so a season picker can be moved into
 * an empty year and back out again.
 *
 * @remarks
 * Backed by the same aggregation as the in-game leaderboards, so the returned
 * `totals` are the row the visitor clicked and the games always sum to it.
 *
 * @example
 * ```typescript
 * const log = await fetchRosterPlayerGameLog('ATL', 'Andre Rison', 33)
 * log.games.length // one row per game he caught a pass in
 * ```
 */
export async function fetchRosterPlayerGameLog(
  teamAbbreviation: string,
  name: string,
  seasonNumber?: number | 'all'
): Promise<RosterPlayerGameLog> {
  const { data } = await api.get<RosterPlayerGameLog>('/stats/players/log', {
    params: {
      team: teamAbbreviation,
      name,
      ...(seasonNumber ? { season: seasonNumber } : {}),
    },
  })
  return data
}

export async function fetchSnapshot(seasonNumber?: number): Promise<LeagueSnapshot> {
  const { data } = await api.get<LeagueSnapshot>('/stats/snapshot', {
    params: seasonNumber ? { season: seasonNumber } : undefined,
  })
  return data
}

export async function fetchChampions(): Promise<Champion[]> {
  const { data } = await api.get<Champion[]>('/champions')
  return data
}

// --- Playoffs --------------------------------------------------------------

/**
 * Every season with a bracket on file.
 *
 * @remarks
 * Cheap enough to fetch alongside a page that only wants to know whether to
 * offer a link, which is what the Hall of Champions and the season archive do.
 */
export async function fetchPlayoffSeasons(): Promise<PlayoffSeasonSummary[]> {
  const { data } = await api.get<PlayoffSeasonSummary[]>('/playoffs')
  return data
}

/**
 * One season's bracket, already resolved into what gets drawn.
 *
 * @returns Null for a season with no bracket, which is the normal case rather
 * than an error worth surfacing.
 */
export async function fetchPlayoffBracket(
  seasonNumber: number
): Promise<PlayoffBracket | null> {
  try {
    const { data } = await api.get<PlayoffBracket>(`/playoffs/${seasonNumber}`)
    return data
  } catch (error) {
    if (isNotFound(error)) return null
    throw error
  }
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { response?: { status?: number } }).response?.status === 404
  )
}

// --- Upload & assignment ---------------------------------------------------

export async function uploadStateFile(file: File, overtimeFile?: File): Promise<UploadResult> {
  const form = new FormData()
  form.append('file', file)
  if (overtimeFile) form.append('overtimeFile', overtimeFile)

  const { data } = await api.post<UploadResult>('/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
  return data
}

export interface AssignPayload {
  homeLeaguePlayerId: number
  awayLeaguePlayerId: number
  homeTeamAbbreviation: string | null
  awayTeamAbbreviation: string | null
  /** Freeform recap, shown on the game page. Omit to leave any existing one. */
  notes?: string | null
  /** Player who used a respin; null when nobody did. */
  respinUsedByLeaguePlayerId?: number | null
}

/** Server rejects with 403 unless the caller is a participant or an admin. */
export async function assignGame(gameId: number, payload: AssignPayload): Promise<GameDetail> {
  const { data } = await api.post<GameDetail>(`/games/${gameId}/assign`, payload)
  return data
}

// --- Admin -----------------------------------------------------------------

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const { data } = await api.get<AdminUser[]>('/admin/users')
  return data
}

export async function setUserAdmin(userId: number, isAdmin: boolean): Promise<void> {
  await api.patch(`/admin/users/${userId}`, { isAdmin })
}

export async function fetchSeasonMemberships(seasonNumber: number): Promise<SeasonMembership[]> {
  const { data } = await api.get<SeasonMembership[]>(`/admin/seasons/${seasonNumber}/memberships`)
  return data
}

export async function saveSeasonMemberships(
  seasonNumber: number,
  assignments: Array<{ leaguePlayerId: number; conference: 'AFC' | 'NFC' }>
): Promise<SeasonMembership[]> {
  const { data } = await api.put<SeasonMembership[]>(
    `/admin/seasons/${seasonNumber}/memberships`,
    { assignments }
  )
  return data
}

/**
 * Proposes a conference split from last season's title game and a three-season
 * record sample. Does not write; the admin still has to save.
 */
export async function proposeConferenceSplit(
  seasonNumber: number,
  leaguePlayerIds: number[]
): Promise<ConferenceDraftProposal> {
  const { data } = await api.post<ConferenceDraftProposal>(
    `/admin/seasons/${seasonNumber}/memberships/randomize`,
    { leaguePlayerIds }
  )
  return data
}

/**
 * The full signup pool for a season.
 *
 * @returns Empty for a season nobody has signed up for, including one that has
 * no row yet — an empty pool is not a missing season.
 */
export async function fetchCurrentSeasonRespins(): Promise<CurrentSeasonRespins> {
  const { data } = await api.get<CurrentSeasonRespins>('/admin/respins')
  return data
}

export async function adjustSeasonRespin(
  leaguePlayerId: number,
  delta: 1 | -1
): Promise<CurrentSeasonRespins> {
  const { data } = await api.post<CurrentSeasonRespins>('/admin/respins', {
    leaguePlayerId,
    delta,
  })
  return data
}

export async function awardMidseasonRespins(): Promise<CurrentSeasonRespins & { awardedCount: number }> {
  const { data } = await api.post<CurrentSeasonRespins & { awardedCount: number }>(
    '/admin/respins/midseason'
  )
  return data
}

export async function fetchSeasonSignups(seasonNumber: number): Promise<SeasonSignup[]> {
  const { data } = await api.get<SeasonSignup[]>(`/admin/seasons/${seasonNumber}/signups`)
  return data
}

/**
 * Enters a player in the pool by hand.
 *
 * @param leaguePlayerId - Any identity on record, including one with no Discord
 * account behind it.
 * @param conference - When set, also places them on that roster in the same
 * call, which is how a late add after kickoff reaches the schedule.
 * @returns The pool as it now stands, so the caller need not refetch.
 * @throws A 403 for non-admins, and a 404 for an unknown player or season.
 */
export async function addSeasonSignup(
  seasonNumber: number,
  leaguePlayerId: number,
  conference?: Conference
): Promise<SeasonSignup[]> {
  const { data } = await api.post<SeasonSignup[]>(`/admin/seasons/${seasonNumber}/signups`, {
    leaguePlayerId,
    ...(conference ? { conference } : {}),
  })
  return data
}

/**
 * Drops a player from the pool.
 *
 * @remarks
 * Leaves their conference assignment alone, if the owner has already made one.
 */
export async function removeSeasonSignup(
  seasonNumber: number,
  leaguePlayerId: number
): Promise<SeasonSignup[]> {
  const { data } = await api.delete<SeasonSignup[]>(
    `/admin/seasons/${seasonNumber}/signups/${leaguePlayerId}`
  )
  return data
}

export async function makeSeasonCurrent(seasonNumber: number): Promise<Season> {
  const { data } = await api.post<Season>(`/admin/seasons/${seasonNumber}/current`)
  return data
}

/** Moves a season through its lifecycle; leaves the current-season flag alone. */
export async function setSeasonStatus(
  seasonNumber: number,
  status: SeasonStatus
): Promise<Season> {
  const { data } = await api.patch<Season>(`/admin/seasons/${seasonNumber}/status`, { status })
  return data
}

/**
 * Sets the two dates a season holds its players to.
 *
 * @param deadlines - The complete desired state; send `null` for a field to
 * clear it, which retroactively un-misses that deadline for everyone.
 * @returns The season as every page will now read it.
 * @throws A 400 when a date is not `YYYY-MM-DD`, when the required-games count
 * is negative, or when the end-of-season date precedes the midseason one.
 */
export async function setSeasonDeadlines(
  seasonNumber: number,
  deadlines: SeasonDeadlines
): Promise<Season> {
  const { data } = await api.patch<Season>(
    `/admin/seasons/${seasonNumber}/deadlines`,
    deadlines
  )
  return data
}

export async function setGameStatus(
  gameId: number,
  status: 'pending' | 'official' | 'void'
): Promise<GameDetail> {
  const { data } = await api.patch<GameDetail>(`/admin/games/${gameId}`, { status })
  return data
}

/** A full hand-entered result. The home side is P1, matching the upload flow. */
export interface GameResultPayload extends AssignPayload {
  homeScore: number
  awayScore: number
  notes?: string | null
  /**
   * Marks the game as postseason, which keeps it out of regular-season
   * standings and badges it wherever it is listed. Omitted leaves the stored
   * flag as it is.
   */
  isPlayoff?: boolean
  /**
   * `YYYY-MM-DD` the game was played. Omit or send `null` to say nothing about
   * it: a new game falls back to today and a correction keeps the date on
   * file. There is no way to clear a date from here, because the whole site
   * orders games by it.
   */
  playedAt?: string | null
}

/** Admin repair of an existing game. Leaves the game's status alone. */
export async function updateGameResult(
  gameId: number,
  payload: GameResultPayload
): Promise<GameDetail> {
  const { data } = await api.put<GameDetail>(`/admin/games/${gameId}/result`, payload)
  return data
}

export interface ManualGamePayload extends GameResultPayload {
  seasonNumber: number
}

/** Records a game played without a save state. Goes straight to official. */
export async function createManualGame(payload: ManualGamePayload): Promise<GameDetail> {
  const { data } = await api.post<GameDetail>('/admin/games', payload)
  return data
}

export async function deleteGame(gameId: number): Promise<void> {
  await api.delete(`/admin/games/${gameId}`)
}

export interface ChampionPayload {
  seasonNumber: number
  championName: string
  championLeaguePlayerId?: number | null
  championTeamAbbreviation?: string | null
  runnerUpName?: string | null
  runnerUpLeaguePlayerId?: number | null
  /** `YYYY-MM-DD`; the server rejects any other format. */
  decidedOn?: string | null
  /** Series scoreline such as `2-1`. */
  seriesResult?: string | null
  blurb?: string | null
}

export async function saveChampion(payload: ChampionPayload): Promise<Champion> {
  const { data } = await api.post<Champion>('/admin/champions', payload)
  return data
}

export async function deleteChampion(id: number): Promise<void> {
  await api.delete(`/admin/champions/${id}`)
}

// --- Admin: playoffs -------------------------------------------------------

export interface PlayoffEditorState {
  /** Null for a season nobody has built a bracket for yet. */
  bracket: PlayoffBracket | null
  championAdvisory: PlayoffChampionAdvisory
}

/**
 * The bracket to edit plus how it compares with the Hall of Champions.
 *
 * @remarks
 * The comparison is advisory: a finished bracket already writes the Hall, and
 * a disagreement never blocks a save.
 */
export async function fetchPlayoffEditor(seasonNumber: number): Promise<PlayoffEditorState> {
  const { data } = await api.get<PlayoffEditorState>(`/admin/seasons/${seasonNumber}/playoffs`)
  return data
}

/** The seeding the season's final standings imply. Read-only until saved. */
export async function fetchPlayoffSeedSuggestions(
  seasonNumber: number
): Promise<PlayoffSeedSuggestions> {
  const { data } = await api.get<PlayoffSeedSuggestions>(
    `/admin/seasons/${seasonNumber}/playoffs/seeding`
  )
  return data
}

export interface PlayoffBracketPayload {
  format: PlayoffFormat
  /** `fixed` locks the draw; `reseed` redraws after each completed round. */
  pairing: PlayoffPairing
  /** 1 or 3; one setting for every round of the season. */
  seriesBestOf: 1 | 3
  notes?: string | null
  entrants: Array<{
    region: 'AFC' | 'NFC' | 'OPEN'
    seed: number
    leaguePlayerId: number
    teamAbbreviation?: string | null
  }>
}

/**
 * Creates or reshapes a season's bracket from its complete field.
 *
 * @remarks
 * Regenerates the opening draw from the seeds. `pairing` decides whether later
 * rounds stay on that path or are redrawn after each round. Results survive
 * wherever the same two players still meet, so correcting one seed does not
 * cost the scores already entered.
 */
export async function savePlayoffBracket(
  seasonNumber: number,
  payload: PlayoffBracketPayload
): Promise<PlayoffBracket> {
  const { data } = await api.put<PlayoffBracket>(
    `/admin/seasons/${seasonNumber}/playoffs`,
    payload
  )
  return data
}

export async function deletePlayoffBracket(seasonNumber: number): Promise<void> {
  await api.delete(`/admin/seasons/${seasonNumber}/playoffs`)
}

export interface PlayoffResultPayload {
  topWins: number
  bottomWins: number
  /** Null leaves the series undecided; the server refuses a winner the score contradicts. */
  winnerEntrantId: number | null
  playedOn?: string | null
  notes?: string | null
}

/**
 * Records one series by hand.
 *
 * @returns The whole bracket, because deciding a series moves its winner into
 * the next round and can clear a result recorded there.
 */
export async function savePlayoffResult(
  matchupId: number,
  payload: PlayoffResultPayload
): Promise<PlayoffBracket> {
  const { data } = await api.patch<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/result`,
    { playedOn: null, notes: null, ...payload }
  )
  return data
}

/**
 * Saves a series note without changing the score.
 *
 * @returns The whole bracket, so the editor preview matches the public page.
 */
export async function savePlayoffMatchupNotes(
  matchupId: number,
  notes: string | null
): Promise<PlayoffBracket> {
  const { data } = await api.patch<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/notes`,
    { notes }
  )
  return data
}

/** Official games between the two players in a series, for the link picker. */
export async function fetchPlayoffGameCandidates(
  matchupId: number
): Promise<PlayoffGameCandidate[]> {
  const { data } = await api.get<PlayoffGameCandidate[]>(
    `/admin/playoffs/matchups/${matchupId}/games`
  )
  return data
}

/** Points a series at a game on file, which also marks it a playoff game. */
export async function attachPlayoffGame(
  matchupId: number,
  gameId: number,
  gameNumber?: number
): Promise<PlayoffBracket> {
  const { data } = await api.post<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/games`,
    { gameId, gameNumber }
  )
  return data
}

/**
 * Unlinks a game from a series.
 *
 * @remarks
 * The game keeps its playoff flag — it was still played in the postseason, and
 * clearing it here would quietly return it to the regular-season standings.
 * The admin game form is where that flag comes off.
 */
export async function detachPlayoffGame(
  matchupId: number,
  gameId: number
): Promise<PlayoffBracket> {
  const { data } = await api.delete<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/games/${gameId}`
  )
  return data
}

/** One game of a series, oriented to the bracket rather than to home and away. */
export interface PlayoffSeriesGamePayload {
  gameNumber: number
  topTeamAbbreviation: string | null
  bottomTeamAbbreviation: string | null
  topScore: number
  bottomScore: number
  /** Which side had the controller; written into the game's home slot. */
  p1: PlayoffSeriesSide
  playedOn?: string | null
  notes?: string | null
}

/**
 * Records one game of a series as a real game row.
 *
 * @remarks
 * The row is created through the same path as any hand-entered game and flagged
 * as a playoff game, so it shows up across the site — Games list, both players'
 * head-to-head, career totals — without the bracket having to publish it.
 * Adding the first game switches the series from its typed score to a score
 * counted off the games.
 */
export async function createPlayoffSeriesGame(
  matchupId: number,
  payload: PlayoffSeriesGamePayload
): Promise<PlayoffBracket> {
  const { data } = await api.post<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/series-games`,
    payload
  )
  return data
}

export async function updatePlayoffSeriesGame(
  matchupId: number,
  gameId: number,
  payload: PlayoffSeriesGamePayload
): Promise<PlayoffBracket> {
  const { data } = await api.patch<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/series-games/${gameId}`,
    payload
  )
  return data
}

/** Deletes the game row outright. A save-state game is unlinked instead. */
export async function deletePlayoffSeriesGame(
  matchupId: number,
  gameId: number
): Promise<PlayoffBracket> {
  const { data } = await api.delete<PlayoffBracket>(
    `/admin/playoffs/matchups/${matchupId}/series-games/${gameId}`
  )
  return data
}

export async function createLeaguePlayer(displayName: string): Promise<void> {
  await api.post('/admin/players', { displayName })
}

export async function linkLeaguePlayer(
  leaguePlayerId: number,
  userId: number | null
): Promise<void> {
  await api.patch(`/admin/players/${leaguePlayerId}/link`, { userId })
}

/**
 * Overrides the name the whole site shows for a player.
 *
 * @param leagueDisplayName - The league name to show, or `null` to clear the
 * override and fall back to the player's current Discord name.
 * @returns The player as every page will now render them.
 *
 * @remarks
 * The server trims, length-checks, and refuses a name another player already
 * renders under, so callers can pass raw input straight from a text field.
 */
export async function setLeagueDisplayName(
  leaguePlayerId: number,
  leagueDisplayName: string | null
): Promise<LeaguePlayer> {
  const { data } = await api.patch<LeaguePlayer>(
    `/admin/players/${leaguePlayerId}/display-name`,
    { leagueDisplayName }
  )
  return data
}

/** Every player an import or a merge has recorded a second spelling for. */
export async function fetchPlayerAliases(): Promise<LeaguePlayerAliases[]> {
  const { data } = await api.get<LeaguePlayerAliases[]>('/admin/players/aliases')
  return data
}

/**
 * Forgets one recorded spelling.
 *
 * @param alias - As listed. It travels in the request body, since an alias may
 * contain emoji or slashes that a URL segment would not survive.
 * @throws A 404 when that player has no such alias.
 *
 * @remarks
 * Irreversible from the site: nothing adds an alias back, and a workbook using
 * the removed spelling will import as a new duplicate player afterwards.
 */
export async function removePlayerAlias(leaguePlayerId: number, alias: string): Promise<void> {
  await api.delete(`/admin/players/${leaguePlayerId}/aliases`, { data: { alias } })
}

export async function fetchUploadLog(): Promise<UploadLogEntry[]> {
  const { data } = await api.get<UploadLogEntry[]>('/admin/uploads')
  return data
}

/** How many uploaded save states are still on hand to re-parse. */
export interface SaveStateInventory {
  stateGames: number
  retained: number
  filesOnDisk: number
  bytesOnDisk: number
}

export interface NameChange {
  side: 'home' | 'away'
  slot: string
  from: string | null
  to: string | null
}

export interface ReparseOutcome {
  gameId: number
  seasonNumber: number
  /** `missing` is the normal result for games uploaded before retention shipped. */
  status: 'updated' | 'unchanged' | 'missing' | 'failed'
  nameChanges: NameChange[]
  /** Reported, never applied — correcting a score is a deliberate admin action. */
  scoreMismatch: string | null
  error: string | null
}

export interface ReparseSummary {
  scanned: number
  updated: number
  unchanged: number
  missing: number
  failed: number
  outcomes: ReparseOutcome[]
}

export async function fetchSaveStateInventory(): Promise<SaveStateInventory> {
  const { data } = await api.get<SaveStateInventory>('/admin/save-states')
  return data
}

/**
 * Rebuilds one game's box score with the current parser.
 *
 * @example
 * ```typescript
 * const outcome = await reparseGame(4431)
 * // outcome.nameChanges lists the slots the parser now names differently.
 * ```
 */
export async function reparseGame(gameId: number): Promise<ReparseOutcome> {
  const { data } = await api.post<ReparseOutcome>(`/admin/games/${gameId}/reparse`)
  return data
}

function filenameFromDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded) return decodeURIComponent(encoded[1])
  const quoted = /filename="([^"]+)"/i.exec(header)
  if (quoted) return quoted[1]
  const plain = /filename=([^;]+)/i.exec(header)
  return plain ? plain[1].trim() : fallback
}

/**
 * Downloads a retained Nestopia save for an uploaded game.
 *
 * @param kind - `2ot` is the second file on a double-overtime result.
 */
export async function downloadGameSaveState(
  gameId: number,
  kind: 'primary' | '2ot' = 'primary'
): Promise<void> {
  const path =
    kind === '2ot'
      ? `/admin/games/${gameId}/save-state/2ot`
      : `/admin/games/${gameId}/save-state`

  try {
    const response = await api.get<Blob>(path, {
      responseType: 'blob',
      timeout: 60000,
      transformResponse: [],
    })
    const filename = filenameFromDisposition(
      response.headers['content-disposition'],
      kind === '2ot' ? `game-${gameId}-2ot.ns` : `game-${gameId}.ns`
    )
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (caught) {
    const blob = (caught as { response?: { data?: unknown } })?.response?.data
    if (blob instanceof Blob) {
      let message = 'The save state could not be downloaded.'
      try {
        const parsed = JSON.parse(await blob.text()) as { error?: unknown }
        if (typeof parsed.error === 'string' && parsed.error) message = parsed.error
      } catch {
        // The body is not JSON; keep the generic message.
      }
      throw new Error(message)
    }
    throw caught
  }
}

/** Replays the parser over the whole archive; run this after a parser fix ships. */
export async function reparseAllSaveStates(): Promise<ReparseSummary> {
  const { data } = await api.post<ReparseSummary>('/admin/save-states/reparse')
  return data
}

export interface ResyncSummary {
  scanned: number
  updated: number
  renamed: number
  filled: number
  byTeam: Array<{ team: string; slots: number }>
  examples: string[]
}

/**
 * Relabels stored box scores from the roster table.
 *
 * @remarks
 * For games whose save state was never retained, where the stat lines are right
 * and only the names attached to them are wrong. Run after the roster data
 * changes.
 */
export async function resyncRosterNames(): Promise<ResyncSummary> {
  const { data } = await api.post<ResyncSummary>('/admin/save-states/resync-names')
  return data
}

/** Admin-only historical import; non-admins receive a 403 from the server. */
export async function importSeasonExcel(
  seasonNumber: number,
  file: File,
  replaceExisting: boolean
): Promise<ImportSummary> {
  const form = new FormData()
  form.append('file', file)
  form.append('replaceExisting', String(replaceExisting))

  const { data } = await api.post<ImportSummary>(
    `/admin/seasons/${seasonNumber}/import-excel`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
  )
  return data
}

/**
 * The homepage poll, or `null` when none is published.
 *
 * @remarks
 * Safe signed out: visitors still see the question and the anonymous counts,
 * with `myOptionId` null.
 */
export async function fetchPoll(): Promise<Poll | null> {
  const { data } = await api.get<Poll | null>('/poll')
  return data
}

/**
 * Casts the caller's one vote.
 *
 * @throws 401 when signed out; 400 when the poll is closed or they already
 * voted for a different option.
 */
export async function voteOnPoll(optionId: number): Promise<Poll> {
  const { data } = await api.post<Poll>('/poll/vote', { optionId })
  return data
}

export async function fetchAdminPoll(): Promise<Poll | null> {
  const { data } = await api.get<Poll | null>('/admin/poll')
  return data
}

export async function publishPoll(input: {
  question: string
  options: string[]
  openFor: PollDuration
}): Promise<Poll> {
  const { data } = await api.post<Poll>('/admin/poll', input)
  return data
}

export async function updatePoll(input: {
  question: string
  options: PollOptionDraft[]
  openFor?: PollDuration
}): Promise<Poll> {
  const { data } = await api.patch<Poll>('/admin/poll', input)
  return data
}

export async function closePoll(): Promise<Poll> {
  const { data } = await api.post<Poll>('/admin/poll/close')
  return data
}

export async function archivePoll(): Promise<void> {
  await api.post('/admin/poll/archive')
}

/**
 * The update currently under the homepage buttons, or `null` when none is showing.
 */
export async function fetchHomepageUpdate(): Promise<LeagueUpdate | null> {
  const { data } = await api.get<LeagueUpdate | null>('/updates/latest')
  return data
}

/** Every league update, newest first. */
export async function fetchLeagueUpdates(): Promise<LeagueUpdate[]> {
  const { data } = await api.get<LeagueUpdate[]>('/updates')
  return data
}

export async function fetchAdminHomepageUpdate(): Promise<LeagueUpdate | null> {
  const { data } = await api.get<LeagueUpdate | null>('/admin/updates/latest')
  return data
}

export async function fetchAdminLeagueUpdates(): Promise<LeagueUpdate[]> {
  const { data } = await api.get<LeagueUpdate[]>('/admin/updates')
  return data
}

export async function publishLeagueUpdate(input: {
  title: string
  body: string
}): Promise<LeagueUpdate> {
  const { data } = await api.post<LeagueUpdate>('/admin/updates', input)
  return data
}

/** Stores an image for a league update and returns the path to embed in the body. */
export async function uploadUpdateImage(file: File): Promise<{ url: string; alt: string }> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<{ url: string; alt: string }>('/admin/updates/images', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
  return data
}

/** Stores a downloadable file for a league update and returns the path to embed. */
export async function uploadUpdateFile(file: File): Promise<{ url: string; filename: string }> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<{ url: string; filename: string }>('/admin/updates/files', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  })
  return data
}

export async function removeHomepageUpdate(): Promise<void> {
  await api.post('/admin/updates/home/remove')
}

export async function editLeagueUpdate(
  id: number,
  input: { title: string; body: string }
): Promise<LeagueUpdate> {
  const { data } = await api.patch<LeagueUpdate>(`/admin/updates/${id}`, input)
  return data
}
